export interface AddressItem {
  label: string;
  city: string;
  uf: string;
  lat: number;
  lng: number;
}

export interface StopPoint {
  id: string;
  address: string;
  city: string;
  lat: number;
  lng: number;
  priority: 'alta' | 'media' | 'normal';
  tw: string;
  weight?: number;
}

export interface VehicleItem {
  id: string;
  name: string;
  plate: string;
  capacity: number;
  unit: string;
  type: string;
  active: boolean;
  driver: string;
  km: number;
  homeDepotId: string;
}

export interface DepotItem {
  id: string;
  name: string;
  address: string;
  city: string;
  lat: number;
  lng: number;
  active: boolean;
  type: 'matriz' | 'distribuicao' | 'crossdock';
}

export interface RouteItem {
  id: string;
  name: string;
  vehicleId: string;
  driver: string;
  status: 'planejada' | 'em_andamento' | 'concluida' | 'cancelada';
  stops: number;
  distance: number; // in km
  duration: number; // in minutes
  savings: number; // percent
  date: string;
  points: StopPoint[];
  _driverState?: {
    completed: number[];
    currentIdx: number;
    gps: { lat: number; lng: number };
  };
}

export interface CostModel {
  fuelPrice: number;
  fuelConsumption: number;
  driverHourlyCost: number;
  depreciationPerKm: number;
  maintenancePerKm: number;
  tollPerKm: number;
  overheadPct: number;
  avgPricePerStop: number;
}

export interface NotificationItem {
  id: string;
  timestamp: number;
  routeId: string;
  stopIdx: number;
  event: 'route_started' | 'next_stop' | 'arrived' | 'delivered' | 'delayed';
  phone: string;
  customerName: string;
  orderRef: string;
  trackingCode: string;
  trackingUrl: string;
  message: string;
  status: 'sent' | 'delivered' | 'read';
  driverName: string;
  vehiclePlate: string;
}

export interface Preferences {
  avoidTolls: boolean;
  prioritizeSpeed: boolean;
  respectTimeWindows: boolean;
  returnToOrigin: boolean;
  maxStopsPerRoute: number;
  avgSpeedUrban: number;
  trafficHeuristic: 'realtime' | 'balanced' | 'historic';
}

export interface AppState {
  user: {
    name: string;
    initials: string;
    email: string;
    company: string;
    role: string;
  };
  depots: DepotItem[];
  vehicles: VehicleItem[];
  routes: RouteItem[];
  costModel: CostModel;
  notifications: NotificationItem[];
  preferences: Preferences;
}

export const BR_ADDRESSES: AddressItem[] = [
  { label: 'Av. Paulista, 1578', city: 'São Paulo', uf: 'SP', lat: -23.5615, lng: -46.656 },
  { label: 'Rua Oscar Freire, 725', city: 'São Paulo', uf: 'SP', lat: -23.5622, lng: -46.6706 },
  { label: 'Av. Faria Lima, 3900', city: 'São Paulo', uf: 'SP', lat: -23.5865, lng: -46.6819 },
  { label: 'Rua Augusta, 2690', city: 'São Paulo', uf: 'SP', lat: -23.5586, lng: -46.6641 },
  { label: 'Av. Rebouças, 3970', city: 'São Paulo', uf: 'SP', lat: -23.5695, lng: -46.6912 },
  { label: 'Av. Ibirapuera, 3103', city: 'São Paulo', uf: 'SP', lat: -23.6098, lng: -46.6642 },
  { label: 'Av. Brig. Luís Antônio, 2013', city: 'São Paulo', uf: 'SP', lat: -23.5726, lng: -46.6558 },
  { label: 'Rua Haddock Lobo, 595', city: 'São Paulo', uf: 'SP', lat: -23.5581, lng: -46.6641 },
  { label: 'Av. dos Bandeirantes, 6580', city: 'São Paulo', uf: 'SP', lat: -23.6118, lng: -46.6893 },
  { label: 'Av. 23 de Maio, 1900', city: 'São Paulo', uf: 'SP', lat: -23.5789, lng: -46.647 },
  { label: 'Rua Vergueiro, 1770', city: 'São Paulo', uf: 'SP', lat: -23.5758, lng: -46.6379 },
  { label: 'Av. Angélica, 1814', city: 'São Paulo', uf: 'SP', lat: -23.5432, lng: -46.6534 },
  { label: 'Rua Consolação, 3416', city: 'São Paulo', uf: 'SP', lat: -23.558, lng: -46.6716 },
  { label: 'Av. Pacaembu, 1102', city: 'São Paulo', uf: 'SP', lat: -23.539, lng: -46.6667 },
  { label: 'Av. Sumaré, 1420', city: 'São Paulo', uf: 'SP', lat: -23.5449, lng: -46.6797 },
  { label: 'Rua Turiassu, 390', city: 'São Paulo', uf: 'SP', lat: -23.5306, lng: -46.6795 },
  { label: 'Av. Cruzeiro do Sul, 1707', city: 'São Paulo', uf: 'SP', lat: -23.514, lng: -46.6266 },
  { label: 'Rua Bela Cintra, 1200', city: 'São Paulo', uf: 'SP', lat: -23.5585, lng: -46.6604 },
  { label: 'Av. Nove de Julho, 5017', city: 'São Paulo', uf: 'SP', lat: -23.5808, lng: -46.6706 },
  { label: 'Rua Teodoro Sampaio, 1442', city: 'São Paulo', uf: 'SP', lat: -23.5551, lng: -46.6812 },
  { label: 'Av. Giovanni Gronchi, 5930', city: 'São Paulo', uf: 'SP', lat: -23.6238, lng: -46.7175 },
  { label: 'Rua Groenlândia, 830', city: 'São Paulo', uf: 'SP', lat: -23.581, lng: -46.6668 },
  { label: 'Av. Água Branca, 1650', city: 'São Paulo', uf: 'SP', lat: -23.5225, lng: -46.6817 },
  { label: 'Rua Domingos de Morais, 2564', city: 'São Paulo', uf: 'SP', lat: -23.5892, lng: -46.6357 }
];

export function sampleStops(indices: number[]): StopPoint[] {
  return indices.map((i, k) => {
    const a = BR_ADDRESSES[i] || BR_ADDRESSES[0];
    return {
      id: `p${i}_${k}`,
      address: a.label,
      city: `${a.city}/${a.uf}`,
      lat: a.lat,
      lng: a.lng,
      priority: k === 0 ? 'alta' : k % 3 === 0 ? 'media' : 'normal',
      tw: k % 3 === 0 ? '09:00–11:00' : 'livre'
    };
  });
}

export const DEFAULT_STATE: AppState = {
  user: {
    name: 'Carlos Andrade',
    initials: 'CA',
    email: 'carlos@expressabc.com.br',
    company: 'Express ABC Logística',
    role: 'Admin'
  },
  depots: [
    { id: 'd1', name: 'CD Sumaré (Matriz)', address: 'Av. Sumaré, 1420', city: 'São Paulo/SP', lat: -23.5449, lng: -46.6797, active: true, type: 'matriz' },
    { id: 'd2', name: 'CD Guarulhos', address: 'Rod. Pres. Dutra, km 210', city: 'Guarulhos/SP', lat: -23.4356, lng: -46.4731, active: true, type: 'distribuicao' },
    { id: 'd3', name: 'CD Alphaville', address: 'Al. Rio Negro, 585', city: 'Barueri/SP', lat: -23.5008, lng: -46.8552, active: true, type: 'distribuicao' }
  ],
  vehicles: [
    { id: 'v1', name: 'Fiorino 01', plate: 'BRA-2E19', capacity: 650, unit: 'kg', type: 'Utilitário', active: true, driver: 'José Silva', km: 34215, homeDepotId: 'd1' },
    { id: 'v2', name: 'Sprinter 02', plate: 'FRT-9K02', capacity: 1400, unit: 'kg', type: 'Van', active: true, driver: 'Marcos R.', km: 87920, homeDepotId: 'd1' },
    { id: 'v3', name: 'HR Bau 03', plate: 'OFC-4T71', capacity: 1500, unit: 'kg', type: 'Baú médio', active: true, driver: 'Ana Paula', km: 112430, homeDepotId: 'd1' },
    { id: 'v4', name: 'Delivery 04', plate: 'CRT-8Z55', capacity: 3500, unit: 'kg', type: 'Baú grande', active: false, driver: '—', km: 198760, homeDepotId: 'd2' },
    { id: 'v5', name: 'Fiorino 05', plate: 'BRT-5F02', capacity: 650, unit: 'kg', type: 'Utilitário', active: true, driver: 'Pedro L.', km: 22040, homeDepotId: 'd2' },
    { id: 'v6', name: 'Master 06', plate: 'KLM-3R91', capacity: 1800, unit: 'kg', type: 'Van', active: true, driver: 'Ricardo O.', km: 56321, homeDepotId: 'd3' }
  ],
  routes: [
    { id: 'r1', name: 'Zona Sul — 15/08 Manhã', vehicleId: 'v3', driver: 'Ana Paula', status: 'em_andamento', stops: 14, distance: 87.4, duration: 198, savings: 22.8, date: '2026-08-15', points: sampleStops([1, 5, 9, 14, 20, 6, 11]) },
    { id: 'r2', name: 'Rota Alphaville — Coleta', vehicleId: 'v2', driver: 'Marcos R.', status: 'concluida', stops: 9, distance: 64.2, duration: 142, savings: 19.1, date: '2026-08-15', points: sampleStops([2, 7, 12, 18, 3]) },
    { id: 'r3', name: 'Centro Expandido', vehicleId: 'v1', driver: 'José Silva', status: 'planejada', stops: 11, distance: 41.6, duration: 115, savings: 24.4, date: '2026-08-15', points: sampleStops([0, 3, 10, 17, 13, 4]) },
    { id: 'r4', name: 'Vila Mariana — Tarde', vehicleId: 'v5', driver: 'Pedro L.', status: 'planejada', stops: 8, distance: 31.2, duration: 92, savings: 18.6, date: '2026-08-15', points: sampleStops([23, 6, 19, 15]) },
    { id: 'r5', name: 'Pinheiros/Perdizes', vehicleId: 'v6', driver: 'Ricardo O.', status: 'concluida', stops: 12, distance: 52.8, duration: 134, savings: 16.7, date: '2026-08-14', points: sampleStops([1, 15, 21, 14, 20]) },
    { id: 'r6', name: 'Zona Leste — Roteiro Manhã', vehicleId: 'v3', driver: 'Ana Paula', status: 'concluida', stops: 16, distance: 104.5, duration: 224, savings: 21.3, date: '2026-08-14', points: sampleStops([16, 10, 22, 18, 4, 12]) },
    { id: 'r7', name: 'Rota Especial — Farmácia', vehicleId: 'v1', driver: 'José Silva', status: 'cancelada', stops: 5, distance: 22.4, duration: 64, savings: 0, date: '2026-08-14', points: sampleStops([0, 7, 4]) }
  ],
  costModel: {
    fuelPrice: 6.19,
    fuelConsumption: 0.11,
    driverHourlyCost: 22.5,
    depreciationPerKm: 0.42,
    maintenancePerKm: 0.18,
    tollPerKm: 0.08,
    overheadPct: 0.15,
    avgPricePerStop: 26.8
  },
  notifications: [],
  preferences: {
    avoidTolls: true,
    prioritizeSpeed: true,
    respectTimeWindows: true,
    returnToOrigin: true,
    maxStopsPerRoute: 50,
    avgSpeedUrban: 32,
    trafficHeuristic: 'balanced'
  }
};

const LS_KEY = 'otimizarota-state-v1';

export function loadStoredState(): AppState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.vehicles && parsed.routes) {
        return {
          ...DEFAULT_STATE,
          ...parsed,
          user: { ...DEFAULT_STATE.user, ...(parsed.user || {}) },
          preferences: { ...DEFAULT_STATE.preferences, ...(parsed.preferences || {}) },
          costModel: { ...DEFAULT_STATE.costModel, ...(parsed.costModel || {}) }
        };
      }
    }
  } catch (e) {
    console.warn('Error reading from localStorage:', e);
  }
  return JSON.parse(JSON.stringify(DEFAULT_STATE));
}

export function saveStoredState(state: AppState) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
    window.dispatchEvent(new Event('otimizarota-state-changed'));
  } catch (e) {
    console.warn('Error saving to localStorage:', e);
  }
}

// Haversine distance in KM
export function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1));
}

export function optimizeRoute(stops: StopPoint[], options: { returnToOrigin?: boolean; prioritizeSpeed?: boolean } = {}) {
  if (!stops.length) return { order: [], distance: 0, duration: 0 };
  const n = stops.length;
  const visited = new Array(n).fill(false);
  const order = [0];
  visited[0] = true;

  for (let i = 1; i < n; i++) {
    const last = stops[order[order.length - 1]];
    let best = -1;
    let bestD = Infinity;
    for (let j = 0; j < n; j++) {
      if (visited[j]) continue;
      const bias = stops[j].priority === 'alta' ? -1.0 : stops[j].priority === 'media' ? -0.4 : 0;
      const d = haversine(last, stops[j]) + bias;
      if (d < bestD) {
        bestD = d;
        best = j;
      }
    }
    order.push(best);
    visited[best] = true;
  }

  // 2-opt refinement
  const dist = (i: number, j: number) => haversine(stops[order[i]], stops[order[j]]);
  let improved = true;
  let pass = 0;
  while (improved && pass < 12) {
    improved = false;
    pass++;
    for (let i = 1; i < order.length - 2; i++) {
      for (let j = i + 1; j < order.length - 1; j++) {
        const d1 = dist(i - 1, i) + dist(j, j + 1);
        const d2 = haversine(stops[order[i - 1]], stops[order[j]]) + haversine(stops[order[i]], stops[order[j + 1]]);
        if (d2 + 1e-6 < d1) {
          const seg = order.slice(i, j + 1).reverse();
          order.splice(i, seg.length, ...seg);
          improved = true;
        }
      }
    }
  }

  let total = 0;
  for (let i = 1; i < order.length; i++) {
    total += haversine(stops[order[i - 1]], stops[order[i]]);
  }
  if (options.returnToOrigin) {
    total += haversine(stops[order[order.length - 1]], stops[order[0]]);
  }

  const avgSpeed = options.prioritizeSpeed ? 38 : 30;
  const duration = Math.round((total / avgSpeed) * 60) + (n - 1) * 3;
  return { order, distance: total, duration };
}

export function fmtKm(v: number) {
  return (v || 0).toFixed(1).replace('.', ',');
}

export function fmtMin(m: number) {
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return (h > 0 ? h + 'h ' : '') + (min < 10 && h > 0 ? '0' : '') + min + 'min';
}

export function formatDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

const BR_FIRST_NAMES = ['Ana', 'Bruno', 'Carla', 'Daniel', 'Eduardo', 'Fernanda', 'Gabriel', 'Helena', 'Isabel', 'João', 'Karina', 'Lucas', 'Mariana', 'Nicolas', 'Otávio', 'Patricia', 'Rafael', 'Sofia', 'Thiago', 'Vanessa'];
const BR_LAST_NAMES = ['Silva', 'Santos', 'Oliveira', 'Souza', 'Rodrigues', 'Ferreira', 'Alves', 'Pereira', 'Lima', 'Gomes', 'Costa', 'Ribeiro', 'Martins', 'Carvalho', 'Almeida'];

export function customerForStop(p: { address?: string }) {
  const key = p.address || '';
  let h = 0;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  h = Math.abs(h);
  const first = BR_FIRST_NAMES[h % BR_FIRST_NAMES.length];
  const last = BR_LAST_NAMES[Math.floor(h / 17) % BR_LAST_NAMES.length];
  const phoneRaw = String(90000000 + (h % 10000000)).padStart(8, '0');
  const phone = `(11) 9${phoneRaw.slice(0, 4)}-${phoneRaw.slice(4, 8)}`;
  const orderRef = 'PED-' + String(200000 + (h % 799999)).padStart(6, '0');
  return { name: `${first} ${last}`, phone, orderRef };
}

export function trackingCodeFor(routeId: string, stopIdx: number) {
  const num = String(routeId).replace(/[^0-9]/g, '');
  const short = num.slice(-6) || '000000';
  return `${short}-${stopIdx}`;
}

export function parseTrackingCode(code: string, routes: RouteItem[]) {
  const m = String(code).match(/^(.+)-(\d+)$/);
  if (!m) return null;
  const numPart = m[1];
  const stopIdx = parseInt(m[2], 10);
  const route = routes.find(r => String(r.id).replace(/[^0-9]/g, '').endsWith(numPart) || String(r.id) === numPart);
  if (!route || !route.points || !route.points[stopIdx]) return null;
  return { route, stopIdx };
}

export const BR_ZONES = [
  { id: 'centro', name: 'Centro Expandido', kmMult: 0.85, complexity: 1.15 },
  { id: 'zona_sul', name: 'Zona Sul', kmMult: 1.05, complexity: 1.0 },
  { id: 'zona_oeste', name: 'Zona Oeste', kmMult: 1.2, complexity: 1.0 },
  { id: 'zona_leste', name: 'Zona Leste', kmMult: 1.4, complexity: 1.1 },
  { id: 'zona_norte', name: 'Zona Norte', kmMult: 1.3, complexity: 1.05 },
  { id: 'alphaville', name: 'Alphaville / Barueri', kmMult: 1.8, complexity: 0.9 },
  { id: 'abc', name: 'ABC (SBC, SA, SCS)', kmMult: 1.6, complexity: 1.05 },
  { id: 'interior', name: 'Interior próximo (até 100km)', kmMult: 3.0, complexity: 0.95 }
];

export const COST_CATEGORIES = [
  { key: 'fuel', label: 'Combustível', color: '#e76f51', formula: 'km × 0,11 L/km × R$/L' },
  { key: 'driver', label: 'Hora-motorista', color: '#0f4c5c', formula: 'horas × R$/h' },
  { key: 'depreciation', label: 'Depreciação', color: '#2a9d8f', formula: 'km × R$ depreciação/km' },
  { key: 'maintenance', label: 'Manutenção', color: '#5c4770', formula: 'km × R$ manutenção/km' },
  { key: 'tolls', label: 'Pedágio', color: '#e9c46a', formula: 'km × R$ pedágio médio/km' },
  { key: 'overhead', label: 'Overhead', color: '#8d99a3', formula: '(direto) × 15%' }
];

export function calculateRouteCost(route: RouteItem, cm: CostModel) {
  const km = route.distance || 0;
  const durationHrs = (route.duration || 0) / 60;
  const stops = Math.max(1, route.stops || 1);
  const fuel = km * cm.fuelConsumption * cm.fuelPrice;
  const driver = durationHrs * cm.driverHourlyCost;
  const depreciation = km * cm.depreciationPerKm;
  const maintenance = km * cm.maintenancePerKm;
  const tolls = km * cm.tollPerKm;
  const subtotal = fuel + driver + depreciation + maintenance + tolls;
  const overhead = subtotal * cm.overheadPct;
  const total = subtotal + overhead;
  return {
    fuel,
    driver,
    depreciation,
    maintenance,
    tolls,
    overhead,
    subtotal,
    total,
    perStop: total / stops
  };
}

export function estimateRouteRevenue(route: RouteItem, cm: CostModel) {
  const stops = Math.max(1, route.stops || 1);
  let h = 0;
  for (const c of String(route.id || '')) h = ((h << 5) - h + c.charCodeAt(0)) | 0;
  const variance = 0.75 + (Math.abs(h) % 100) / 100 * 0.6;
  return stops * cm.avgPricePerStop * variance;
}

export function routeProfitability(route: RouteItem, cm: CostModel) {
  const cost = calculateRouteCost(route, cm);
  const revenue = estimateRouteRevenue(route, cm);
  const profit = revenue - cost.total;
  const margin = revenue > 0 ? profit / revenue : 0;
  return { cost, revenue, profit, margin };
}
